package one.healthium.ui.compose.components

import androidx.compose.foundation.text.InlineTextContent
import androidx.compose.foundation.text.appendInlineContent
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black
import one.healthium.ui.compose.theme.blue_2
import one.healthium.ui.compose.theme.green_1
import one.healthium.ui.compose.theme.green_7
import one.healthium.ui.compose.theme.orange
import one.healthium.ui.compose.theme.orange_1
import one.healthium.ui.compose.theme.red_1
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow

@Composable
fun AppText(
    modifier: Modifier = Modifier,
    text: String?,
    style: TextStyle = LocalTextStyle.current,
    color: Color = Color.Unspecified,
    textAlign: TextAlign? = null,
    lineHeight: TextUnit = TextUnit.Unspecified,
    inlineContent: Map<String, InlineTextContent> = mapOf(),
) {
    if (text.isNullOrBlank()) return
    val colorTheme = ColorThemeStyle(primary = AppTheme.colors.primary, secondary = AppTheme.colors.secondary)

    val styledText = remember(text, style) { parseText(text, style, colorTheme) }
    Text(
        modifier = modifier,
        text = styledText,
        style = style,
        color = color,
        textAlign = textAlign,
        inlineContent = inlineContent,
        lineHeight = lineHeight,
    )
}

private fun parseText(text: String, style: TextStyle, colorTheme: ColorThemeStyle): AnnotatedString {
    return buildAnnotatedString {
        var startIndex = 0

        var matchToLink: MatchResult? = null

        Regex("<[^>]+>").findAll(text).forEach { matchResult ->
            val match = matchResult.value

            if (match == "<animatable_in_3months_short>") {
                append(text.substring(startIndex, matchResult.range.first))
                appendInlineContent("animatable_in_3months_short")

                startIndex = matchResult.range.last + 1

                return@forEach
            }

            val previousMatch = matchToLink
            if (previousMatch != null) {
                if (previousMatch.value == match) {
                    val rule = rules.find { it.pattern == match }
                    if (rule != null) {
                        val updatedStyle = style.copy(
                            fontSize = rule.fontSize ?: style.fontSize,
                            color = rule.color ?: rule.colorTheme?.resolve(colorTheme) ?: style.color,
                            fontWeight = rule.fontWeight ?: style.fontWeight,
                        )

                        withStyle(updatedStyle.toSpanStyle()) {
                            append(text.substring(previousMatch.range.last + 1, matchResult.range.first))
                        }
                    } else {
                        append(text.substring(previousMatch.range.first, matchResult.range.last + 1))
                    }
                    matchToLink = null
                }
            } else {
                val firstIndex = matchResult.range.first
                if (firstIndex > startIndex) {
                    append(text.substring(startIndex, firstIndex))
                }

                matchToLink = matchResult
            }

            startIndex = matchResult.range.last + 1
        }

        append(text.substring(startIndex))
    }
}

private data class StyleRule(
    val pattern: String,
    val fontSize: TextUnit?,
    val color: Color?,
    val fontWeight: FontWeight?,
    val colorTheme: ColorTheme? = null,
)

private val rules = listOf(
    StyleRule("<Bold>", null, null, FontWeight.Bold),
    StyleRule("<Primary>", null, null, null, ColorTheme.Primary),
    StyleRule("<PrimaryBold>", null, null, FontWeight.Bold, ColorTheme.Primary),
    StyleRule("<Secondary>", null, null, FontWeight.Bold, ColorTheme.Secondary),
    StyleRule("<SecondaryBold>", null, null, FontWeight.Bold, ColorTheme.Secondary),
    StyleRule("<Default_20>", 20.sp, null, null),
    StyleRule("<Green>", null, green_7, null),
    StyleRule("<OrangeBold>", null, orange, FontWeight.Bold),

    StyleRule("<OrangeBold_18>", 18.sp, orange, FontWeight.Bold),
    StyleRule("<OrangeBold_20>", 20.sp, orange, FontWeight.Bold),
    StyleRule("<OrangeBold_24>", 24.sp, orange, FontWeight.Bold),
    StyleRule("<OrangeBold_30>", 30.sp, orange, FontWeight.Bold),
    StyleRule("<OrangeBold_35>", 30.sp, orange, FontWeight.ExtraBold),
    StyleRule("<OrangeHeavy_20>", 20.sp, orange, FontWeight.ExtraBold),
    StyleRule("<WhiteBold_20>", 20.sp, white, FontWeight.Bold),
    StyleRule("<WhiteBold_30>", 30.sp, white, FontWeight.Bold),
    StyleRule("<WhiteHeavy_58>", 58.sp, white, FontWeight.ExtraBold),
    StyleRule("<BlackBold_17>", 17.sp, black, FontWeight.Bold),
    StyleRule("<Yellow_17>", 17.sp, yellow, FontWeight.Medium),
    StyleRule("<BMI_Low>", 14.sp, blue_2, FontWeight.Bold),
    StyleRule("<BMI_Normal>", 14.sp, green_1, FontWeight.Bold),
    StyleRule("<BMI_Overweight>", 14.sp, orange_1, FontWeight.Bold),
    StyleRule("<BMI_Obese>", 14.sp, red_1, FontWeight.Bold),
)

private fun ColorTheme.resolve(colorTheme: ColorThemeStyle): Color {
    return when (this) {
        ColorTheme.Primary -> colorTheme.primary
        ColorTheme.Secondary -> colorTheme.secondary
    }
}

private enum class ColorTheme {
    Primary,
    Secondary,

}

private data class ColorThemeStyle(
    val primary: Color,
    val secondary: Color,
)